import { FlaskConical, ListChecks, Package, Plus, RefreshCw, Sparkles, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { Button, SelectField, TextField } from '@krp/ui-kit'

import { ActionError } from '../../../shared/ActionError'
import { QueryState } from '../../../shared/QueryState'
import { TEXT_KIND_TITLES } from '../model/selectors'
import { TEXT_QUERY_KINDS, type TestingAccess, type TextQueriesActions, type TextQueriesVm, type TextQueryKind } from '../model/types'
import { TestingGate } from './TestingParts'

const KIND_OPTIONS = TEXT_QUERY_KINDS.map((kind) => ({
  value: kind,
  label: TEXT_KIND_TITLES[kind],
}))
const ALL = 'all'

/**
 * Поиск по тексту · Запросы (RP-FB34): что ищет продавец и какой товар должен
 * найтись. Запросы генерируются из тестовых товаров; свои остаются при новой генерации.
 */
export function TextQueriesScreen({ access, vm, actions }: { access: TestingAccess; vm: TextQueriesVm; actions: TextQueriesActions }) {
  const [filter, setFilter] = useState<string>(ALL)
  const [text, setText] = useState('')
  const [productId, setProductId] = useState('')
  const [kind, setKind] = useState<TextQueryKind>('other')
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const rows = filter === ALL ? vm.rows : vm.rows.filter((row) => row.kind === filter)
  const filterOptions = [
    { value: ALL, label: `Все виды (${vm.rows.length})` },
    ...TEXT_QUERY_KINDS.filter((item) => (vm.counts[item] ?? 0) > 0).map((item) => ({
      value: item,
      label: `${TEXT_KIND_TITLES[item]} (${vm.counts[item]})`,
    })),
  ]
  const chosenProduct = productId === '' ? (vm.products[0]?.id ?? '') : productId

  return (
    <TestingGate access={access}>
      <section aria-labelledby="platform-text-queries-title" className="platform-testing">
        <header className="platform-resources__toolbar">
          <h2 id="platform-text-queries-title">Поиск по тексту · Запросы</h2>
          <div className="platform-testing__row">
            <Button
              variant="ghost"
              icon={<Package size={16} aria-hidden="true" />}
              onClick={actions.openProducts}
              actionId="platform.testing-text-queries.open-products"
            >
              Товары
            </Button>
            <Button
              variant="ghost"
              icon={<FlaskConical size={16} aria-hidden="true" />}
              onClick={actions.openCheck}
              actionId="platform.testing-text-queries.open-check"
            >
              Проверить запрос
            </Button>
            <Button
              variant="ghost"
              icon={<ListChecks size={16} aria-hidden="true" />}
              onClick={actions.openRuns}
              actionId="platform.testing-text-queries.open-runs"
            >
              Замеры
            </Button>
            <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="platform.testing-text-queries.reload">
              Обновить
            </Button>
          </div>
        </header>
        <p className="platform-testing__note">
          Запросы ищут среди тестовых товаров тем же путём, что номенклатура: штрихкод и артикул целиком — из базы, длинные цифры без совпадения — пусто,
          остальное — служба text-search.
        </p>
        <ActionError error={vm.action.error} onReload={actions.reload} />

        <section aria-label="Генерация">
          <h3>Сгенерировать из тестовых товаров</h3>
          <p className="platform-testing__note">
            Из каждого тестового товара («Товары» вверху): название, часть названия, опечатка, раскладка, артикул и каждый штрихкод. Прежние
            сгенерированные запросы заменяются, свои остаются.
          </p>
          <div className="platform-testing__actions">
            <Button
              variant="primary"
              icon={<Sparkles size={16} aria-hidden="true" />}
              busy={vm.action.busy}
              disabled={vm.products.length === 0}
              disabledReason={vm.products.length === 0 ? 'Сначала добавьте тестовые товары — кнопка «Товары» вверху' : null}
              onClick={actions.generate}
              actionId="platform.testing-text-queries.generate"
            >
              Сгенерировать
            </Button>
          </div>
          {vm.generated !== null && <p role="status">{vm.generated}</p>}
        </section>

        <section aria-label="Свой запрос">
          <h3>Свой запрос</h3>
          <p className="platform-testing__note">Синоним, сокращение, как пишут в магазине — то, чего генерация не придумает.</p>
          <form
            onSubmit={(event) => {
              event.preventDefault()
              if (text.trim() === '' || chosenProduct === '') return
              // Поле очищается только когда сервер принял запрос: при отказе набранное остаётся.
              void actions.add({ text: text.trim(), productId: chosenProduct, kind }).then((saved) => {
                if (saved) setText('')
              })
            }}
          >
            <div className="platform-testing__fields">
              <TextField label="Запрос" placeholder="как ищет продавец" value={text} onChange={setText} />
              <SelectField
                label="Должен найтись"
                value={chosenProduct}
                options={vm.products.map((product) => ({
                  value: product.id,
                  label: product.name,
                }))}
                onChange={setProductId}
                actionId="platform.testing-text-queries.product"
              />
              <SelectField
                label="Вид"
                value={kind}
                options={KIND_OPTIONS}
                onChange={(value) => setKind(TEXT_QUERY_KINDS.find((item) => item === value) ?? 'other')}
                actionId="platform.testing-text-queries.kind"
              />
            </div>
            <div className="platform-testing__actions">
              <Button
                type="submit"
                icon={<Plus size={16} aria-hidden="true" />}
                disabled={vm.action.busy || text.trim() === '' || chosenProduct === ''}
                disabledReason={chosenProduct === '' ? 'Нет тестовых товаров' : text.trim() === '' ? 'Введите запрос' : null}
                actionId="platform.testing-text-queries.add"
              >
                Добавить запрос
              </Button>
            </div>
          </form>
        </section>

        <section aria-label="Запросы">
          <header className="platform-resources__toolbar">
            <h3>Запросы</h3>
            <SelectField label="Вид запроса" value={filter} options={filterOptions} onChange={setFilter} actionId="platform.testing-text-queries.filter" />
          </header>
          {vm.query.status !== 'ready' ? (
            <QueryState query={vm.query} emptyText="Запросов ещё нет — сгенерируйте их из тестовых товаров" onRetry={actions.reload} />
          ) : (
            <div className="platform-sync__table-wrap">
              <table className="platform-sync__table">
                <thead>
                  <tr>
                    <th>Запрос</th>
                    <th>Вид</th>
                    <th>Должен найтись</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <code>{row.text}</code>
                      </td>
                      <td>
                        {row.kindTitle}
                        <div className="platform-testing__note">{row.originText}</div>
                      </td>
                      <td>{row.productName}</td>
                      <td>
                        <div className="platform-testing__row">
                          <Button
                            variant="ghost"
                            icon={<FlaskConical size={16} aria-hidden="true" />}
                            aria-label={`Проверить «${row.text}»`}
                            onClick={() => actions.check(row.id)}
                            actionId="platform.testing-text-queries.check"
                          >
                            Проверить
                          </Button>
                          {confirmDelete === row.id ? (
                            <>
                              <Button
                                variant="danger"
                                icon={<Trash2 size={16} aria-hidden="true" />}
                                onClick={() => {
                                  setConfirmDelete(null)
                                  actions.remove(row.id)
                                }}
                                actionId="platform.testing-text-queries.delete"
                              >
                                Подтвердить удаление
                              </Button>
                              <Button variant="ghost" onClick={() => setConfirmDelete(null)} actionId="platform.testing-text-queries.delete">
                                Не удалять
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              icon={<Trash2 size={16} aria-hidden="true" />}
                              aria-label={`Удалить «${row.text}»`}
                              onClick={() => setConfirmDelete(row.id)}
                              actionId="platform.testing-text-queries.delete"
                            >
                              Удалить
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </section>
    </TestingGate>
  )
}
